<template>
  <v-flex xs12>
    <h3 class="pb-2 mr-2 d-inline-block">
      <span class="mr-1" style="font-weight: 700;">Step 2</span>-
      Select a table to import into
    </h3>
    <v-flex xs12 px-4 pt-4 mb-4 class="white-bg elevation-2 rounded-3">
      <v-autocomplete
        box
        dense
        persistent-hint
        :items="entityTables"
        label="Select table"
        v-model="tableListSelected"
        no-data-text="No entity tables on this template"
        append-icon="keyboard_arrow_down"
        item-text="name"
        return-object
      />
    </v-flex>

    <v-flex xs12 v-if="tableListSelected">
      <h3 class="pb-2 mr-2 d-inline-block">
        <span class="mr-1" style="font-weight: 700;">Step 3</span>-
        Select CSV to upload
      </h3>
      <v-flex xs12 px-4 py-4 mb-4 class="white-bg elevation-2 rounded-3">
        <v-btn
          color="secondary"
          class="text-none ma-0"
          depressed
          :loading="isSelecting"
          @click="onButtonClick"
        >
          <v-icon left>cloud_upload</v-icon>
          {{ buttonText }}
        </v-btn>
        <input
          ref="uploader"
          class="d-none"
          type="file"
          accept=".csv"
          @change="onFileChanged"
        />
        <p v-if="parseError" class="red--text mt-3 mb-0">{{ parseError }}</p>
      </v-flex>
    </v-flex>

    <v-flex xs12 v-if="mappings.length > 0">
      <h3 class="pb-2 mr-2 d-inline-block">
        <span class="mr-1" style="font-weight: 700;">Step 4</span>-
        Match CSV columns to case keys and table fields
      </h3>
      <v-flex xs12 px-4 py-4 mb-4 class="white-bg elevation-2 rounded-3">
        <p class="mb-3">
          Map case-level primary columns and columns for
          <strong>{{ tableListSelected.name }}</strong>.
          Use Exclude to skip a column.
        </p>
        <csv-field-mapping
          :mappings="mappings"
          :template-fields="templateFields"
          @change-mapping="onMappingChange"
        />
      </v-flex>
    </v-flex>

    <v-flex xs12 v-if="mappedCaseFieldNames.length > 0">
      <h3 class="pb-2 mr-2 d-inline-block">
        <span class="mr-1" style="font-weight: 700;">Step 5</span>-
        Which mapped columns identify the parent case?
      </h3>
      <v-flex xs12 px-4 py-3 mb-4 class="white-bg elevation-2 rounded-3">
        <v-chip
          color="secondary"
          text-color="white"
          v-for="(fieldName, index) in mappedCaseFieldNames"
          :key="'case-' + index"
        >
          <v-switch
            :input-value="!!casePrimarySelected[fieldName]"
            class="mt-2"
            @change="toggleCasePrimary(fieldName, $event)"
          />
          {{ fieldName }}
        </v-chip>
      </v-flex>
    </v-flex>

    <v-flex xs12 v-if="mappedEntityFieldNames.length > 0">
      <h3 class="pb-2 mr-2 d-inline-block">
        <span class="mr-1" style="font-weight: 700;">Step 6</span>-
        Select the unique key field for table rows
      </h3>
      <v-flex xs12 px-4 py-3 mb-4 class="white-bg elevation-2 rounded-3">
        <v-autocomplete
          box
          dense
          persistent-hint
          :items="mappedEntityFieldNames"
          label="Select key"
          v-model="tableFieldsSelectedPrimary"
          no-data-text="No results"
          append-icon="keyboard_arrow_down"
        />
      </v-flex>
    </v-flex>
  </v-flex>
</template>

<script>
import Papa from "papaparse"
import { mapState } from "vuex"
import CsvFieldMapping from "@/components/import/CsvFieldMapping.vue"
import {
  collectAtomicFields,
  collectEntityFields,
  collectEntityTables,
  matchCsvHeaders,
  mappedFieldNames,
  suggestDefaultPrimaries,
  applyMappingOverride,
  pickBestEntityTable,
} from "@/helpers/csvFieldMatch"

export default {
  name: "tabular-import",
  components: { CsvFieldMapping },
  data: function () {
    return {
      tableListSelected: null,
      isSelecting: false,
      selectedFile: null,
      parseError: null,
      mappings: [],
      csvRows: [],
      casePrimarySelected: {},
      tableFieldsSelectedPrimary: null,
      autoPickedTable: false,
    }
  },
  computed: {
    ...mapState({
      caseType: function (state) {
        return state.caseTypes.current
      },
    }),
    entityTables: function () {
      const schema = this.caseType && this.caseType.currentSchema
      return collectEntityTables(schema)
    },
    caseFields: function () {
      const schema = this.caseType && this.caseType.currentSchema
      return collectAtomicFields(schema).filter(function (field) {
        return field.type !== "entity"
      })
    },
    entityFields: function () {
      return collectEntityFields(this.tableListSelected)
    },
    templateFields: function () {
      // Case-level fields first, then entity columns (prefixed label via display).
      const entity = this.entityFields.map(function (field) {
        return Object.assign({}, field, {
          display_name: field.display_name
            ? field.display_name + " (table)"
            : field.name + " (table)",
        })
      })
      // Avoid name collisions: entity field names that collide with case fields
      // still map by name (server stores them on the entity row).
      return this.caseFields.concat(entity)
    },
    mappedNames: function () {
      return mappedFieldNames(this.mappings)
    },
    mappedCaseFieldNames: function () {
      const caseNames = {}
      this.caseFields.forEach(function (f) {
        caseNames[f.name] = true
      })
      return this.mappedNames.filter(function (name) {
        return caseNames[name]
      })
    },
    mappedEntityFieldNames: function () {
      const entityNames = {}
      this.entityFields.forEach(function (f) {
        entityNames[f.name] = true
      })
      return this.mappedNames.filter(function (name) {
        return entityNames[name]
      })
    },
    buttonText: function () {
      return this.selectedFile
        ? this.selectedFile.name
        : "Select CSV from your computer"
    },
  },
  watch: {
    entityTables: {
      immediate: true,
      handler: function (tables) {
        if (!this.tableListSelected && tables && tables.length === 1) {
          this.tableListSelected = tables[0]
          this.autoPickedTable = true
        }
      },
    },
    tableListSelected: function (table) {
      this.$emit("update-import-data", {
        name: "table",
        value: table ? table.name : null,
      })
      if (this.selectedFile) {
        this.parseCsv(this.selectedFile)
      } else {
        this.emitSelections()
      }
    },
    mappings: function () {
      this.emitSelections()
    },
    casePrimarySelected: {
      deep: true,
      handler: function () {
        this.emitSelections()
      },
    },
    tableFieldsSelectedPrimary: function () {
      this.emitSelections()
    },
  },
  methods: {
    onButtonClick: function () {
      this.isSelecting = true
      window.addEventListener(
        "focus",
        function () {
          this.isSelecting = false
        }.bind(this),
        { once: true },
      )
      this.$refs.uploader.click()
    },
    onFileChanged: function (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.selectedFile = file
      this.parseError = null
      // Auto-pick best entity table from headers if user has not chosen yet.
      Papa.parse(file, {
        header: true,
        preview: 1,
        skipEmptyLines: true,
        complete: function (preview) {
          const headers =
            preview.meta && preview.meta.fields ? preview.meta.fields : []
          if (!this.autoPickedTable || !this.tableListSelected) {
            const best = pickBestEntityTable(this.entityTables, headers)
            if (best) {
              this.tableListSelected = best
              this.autoPickedTable = true
            }
          }
          this.parseCsv(file)
        }.bind(this),
        error: function (err) {
          this.parseError = (err && err.message) || "Unable to parse CSV"
        }.bind(this),
      })
    },
    parseCsv: function (file) {
      const self = this
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: function (results) {
          if (results.errors && results.errors.length > 0) {
            self.parseError = results.errors[0].message || "Unable to parse CSV"
            self.mappings = []
            self.csvRows = []
            self.emitReady(false)
            return
          }
          const headers = results.meta && results.meta.fields ? results.meta.fields : []
          const rows = results.data || []
          const sample = rows.length > 0 ? rows[0] : {}
          self.csvRows = rows
          self.mappings = matchCsvHeaders(headers, self.templateFields, sample)
          const caseNames = self.mappedCaseFieldNames
          const suggested = suggestDefaultPrimaries(
            caseNames,
            self.caseType && self.caseType.uniqueIdentifierField,
          )
          const primaries = {}
          caseNames.forEach(function (name) {
            primaries[name] = suggested.indexOf(name) >= 0
          })
          self.casePrimarySelected = primaries
          const entityNames = self.mappedEntityFieldNames
          if (
            !self.tableFieldsSelectedPrimary ||
            entityNames.indexOf(self.tableFieldsSelectedPrimary) < 0
          ) {
            self.tableFieldsSelectedPrimary =
              entityNames.length > 0 ? entityNames[0] : null
          }
          self.emitSelections()
        },
        error: function (err) {
          self.parseError = (err && err.message) || "Unable to parse CSV"
          self.mappings = []
          self.csvRows = []
          self.emitReady(false)
        },
      })
    },
    onMappingChange: function (payload) {
      this.mappings = applyMappingOverride(
        this.mappings,
        payload.index,
        payload.fieldName,
        this.templateFields,
      )
      const caseNames = this.mappedCaseFieldNames
      const next = {}
      caseNames.forEach(function (name) {
        next[name] = !!this.casePrimarySelected[name]
      }.bind(this))
      if (
        Object.keys(next).every(function (k) {
          return !next[k]
        })
      ) {
        const suggested = suggestDefaultPrimaries(
          caseNames,
          this.caseType && this.caseType.uniqueIdentifierField,
        )
        suggested.forEach(function (name) {
          next[name] = true
        })
      }
      this.casePrimarySelected = next
      const entityNames = this.mappedEntityFieldNames
      if (
        this.tableFieldsSelectedPrimary &&
        entityNames.indexOf(this.tableFieldsSelectedPrimary) < 0
      ) {
        this.tableFieldsSelectedPrimary =
          entityNames.length > 0 ? entityNames[0] : null
      }
    },
    toggleCasePrimary: function (fieldName, value) {
      this.$set(this.casePrimarySelected, fieldName, !!value)
    },
    emitSelections: function () {
      const entityNameSet = {}
      this.entityFields.forEach(function (f) {
        entityNameSet[f.name] = true
      })
      const caseNameSet = {}
      this.caseFields.forEach(function (f) {
        caseNameSet[f.name] = true
      })

      const fields = []
      this.mappings.forEach(function (row) {
        if (row.fieldName && entityNameSet[row.fieldName]) {
          const match = this.entityFields.filter(function (f) {
            return f.name === row.fieldName
          })[0]
          if (match) fields.push(match)
        }
      }.bind(this))

      const primaries = []
      Object.keys(this.casePrimarySelected).forEach(function (key) {
        if (this.casePrimarySelected[key] && caseNameSet[key]) {
          const match = this.caseFields.filter(function (f) {
            return f.name === key
          })[0]
          if (match) primaries.push(match)
        }
      }.bind(this))

      this.$emit("update-import-data", { name: "fields", value: fields })
      this.$emit("update-import-data", { name: "primaries", value: primaries })
      this.$emit("update-import-data", {
        name: "tablePrimary",
        value: this.tableFieldsSelectedPrimary,
      })
      this.$emit("update-import-data", {
        name: "table",
        value: this.tableListSelected ? this.tableListSelected.name : null,
      })
      this.$emit("update-import-data", { name: "mappings", value: this.mappings })
      this.$emit("update-import-data", { name: "csvRows", value: this.csvRows })
      this.$emit("update-import-data", {
        name: "selectedFile",
        value: this.selectedFile,
      })
      this.emitReady(
        fields.length > 0 &&
          primaries.length > 0 &&
          !!this.tableFieldsSelectedPrimary &&
          !!this.tableListSelected,
      )
    },
    emitReady: function (ready) {
      this.$emit("ready-to-upload", ready)
    },
  },
}
</script>
